import { FileReference } from '@kaizenplatform/belfast-entity';
import { createPath } from 'history';
import { parse } from 'qs';
import { ClassAttributes, createElement, useContext, useEffect } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import AmazonAdAccount from '../../entity/AmazonAdAccount';
import EditModificationRequestItem from '../../entity/EditModificationRequestItem';
import FacebookAdAccount from '../../entity/FacebookAdAccount';
import TiktokAdAccount from '../../entity/TiktokAdAccount';
import UploadingFile from '../../entity/UploadingFile';
import YahooAdAccount from '../../entity/YahooAdAccount';
import YoutubeChannel from '../../entity/YoutubeChannel';
import {
  BusinessRegionAdRegionKey,
  DeliverableForCraetionDetailFragment,
  File,
  ModificationRequestFeeType,
  OrganizationDetailFragment,
} from '../../generated/graphql';
import mergeUrlQuery from '../../utility/mergeUrlQuery';
import { context as ConfigContext } from '../context/config';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import Page from '../page/ShowCreationPage';

type RouteParams = {
  creationId: string;
};

type Props = ClassAttributes<HTMLElement> & RouteComponentProps<RouteParams>;

const CreationDetailPage = ({ match }: Props) => {
  const dependency = useContext(DependencyContext);
  const state = useContext(StateContext);
  const history = useContext(HistoryContext);
  const config = useContext(ConfigContext);

  const query = parse(state.location.search.slice(1));
  const organizationId = query.org;
  let organization: OrganizationDetailFragment;

  const region: BusinessRegionAdRegionKey =
    state.lastAccessedOrganization != null && state.lastAccessedOrganization.businessRegion.key
      ? state.lastAccessedOrganization.businessRegion.key
      : 'jp';

  const {
    modification_request: selectedModificationRequestId,
    modification_request_issue: selectedModificationRequestIssueId,
  } = parse(state.location.search.slice(1));

  useEffect(() => {
    // In-Page Link
    if (
      (state.postedModificationRequests.modificationRequests!.length > 0 && selectedModificationRequestId) ||
      selectedModificationRequestIssueId
    ) {
      const targetElement = document.getElementById(
        selectedModificationRequestId ? 'modificationRequest' : selectedModificationRequestIssueId,
      );

      if (targetElement) {
        const rectTop = targetElement.getBoundingClientRect().top;
        const offsetTop = window.pageYOffset;
        const headBarHeight = 60;
        const top = rectTop + offsetTop - headBarHeight;

        window.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.creation.fetching === 0 && state.postedModificationRequests.fetching === 0]);

  if (state.lastAccessedOrganization) {
    organization = state.lastAccessedOrganization;
  } else {
    organization = state.availableOrganizations.items[0];
  }

  // クエパラにorganizationIdが存在しない場合はリダイレクトする
  if (!organizationId && organization) {
    return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
  }

  // 提案中のcreationについては詳細画面の閲覧を不可とする
  if (
    state.creation.creation &&
    state.creation.creation.state === 'in_proposal' &&
    !state.creation.creation.submittedAt
  ) {
    return <Redirect to="/latest-insights" />;
  }

  const profileUrl = config.PROFILE_WEB_URL;

  const productPricing = state.acct.productPricing;

  return (
    <Page
      loading={state.creation.fetching >= 1}
      creation={state.creation.creation}
      region={region}
      profileUrl={profileUrl}
      uploadedAdPlatformKeys={state.creativeSending.uploadedAdPlatformKeys}
      organizationId={organization.id}
      isPointRequired={organization ? typeof organization.pointBalance === 'number' : false}
      productPricing={productPricing || undefined}
      pointBalance={organization?.pointBalance ?? null}
      // Modifiction Request
      postedModificationRequests={state.postedModificationRequests}
      modificationRequestEditing={state.modificationRequestEditing}
      onPushInputtingItem={async (
        modificationRequestId: string,
        bucketId: string | null,
        inputDuration: string,
        inputComment: string,
        files: Array<FileReference>,
      ) =>
        await dependency.modificationRequestUsecase.createModificationRequestItem(
          modificationRequestId,
          bucketId,
          inputDuration,
          inputComment,
          files,
        )
      }
      onClickRequestStart={() => dependency.modificationRequestUsecase.createModificationRequestAsDraft()}
      onSelectDeliverable={(deliverableId: string) =>
        dependency.modificationRequestUsecase.selectedDeliverable(deliverableId)
      }
      onSelectFeeType={(feeType: ModificationRequestFeeType) =>
        dependency.modificationRequestUsecase.selectedFeeType(feeType)
      }
      onUploadModificationRequestAssetFiles={(files: UploadingFile[]) => {
        dependency.modificationRequestUsecase.uploadAssetFiles(files);
      }}
      onOpenEditModificationRequestDialog={(index: number) =>
        dependency.modificationRequestUsecase.setEditModificationRequestItem(index)
      }
      onCloseEditModificationRequestDialog={() =>
        dependency.modificationRequestUsecase.disposeEditModificationRequestItem()
      }
      onRemoveEditAssetFile={(fileId: string) => dependency.modificationRequestUsecase.removeEditAssetFile(fileId)}
      onSubmitEditModificationRequestDialog={(
        index: number,
        editModificationRequestItem: EditModificationRequestItem,
        modificationRequestItemId: string,
        duration: string,
        comment: string,
      ) =>
        dependency.modificationRequestUsecase.editModificationRequestItem(
          index,
          editModificationRequestItem,
          modificationRequestItemId,
          duration,
          comment,
        )
      }
      onRequestEditAssetFiles={(files: UploadingFile[], bucketId: string) =>
        dependency.modificationRequestUsecase.uploadEditAssetFiles(files, bucketId)
      }
      onRemoveModificationRequestItem={(modificationRequestItemId: string, index: number) =>
        dependency.modificationRequestUsecase.deleteModificationRequestItem(modificationRequestItemId, index)
      }
      onRemoveAssetFile={(bucketId: string, fileId: string) =>
        dependency.modificationRequestUsecase.removeAssetFile(bucketId, fileId)
      }
      onRemoveAllAssetFile={() => dependency.modificationRequestUsecase.removeAllAssetFile()}
      onRemoveAssetIssueFile={(bucketId: string, fileId: string, itemId: string) => {
        dependency.modificationRequestUsecase.removeAssetIssueFile(bucketId, fileId, itemId);
      }}
      onRemoveAssetIssueCommentFile={(bucketId: string, fileId: string, issueId: string) => {
        dependency.modificationRequestUsecase.removeAssetIssueCommentFile(bucketId, fileId, issueId);
      }}
      onCancelModificationrequestEditing={() =>
        dependency.modificationRequestUsecase.cancelCreatingDraftModificationRequest()
      }
      onRequestSendModificationRequest={async () =>
        await dependency.modificationRequestUsecase.submitModificationRequest()
      }
      onUploadIssueCommentAssetFiles={(files: UploadingFile[], deliverableId: string, issueId: string) => {
        dependency.modificationRequestUsecase.uploadIssueCommentAssetFiles(files, deliverableId, issueId);
      }}
      onUploadIssueAssetFiles={(files: UploadingFile[], bucketId: string, itemId: string) => {
        dependency.modificationRequestUsecase.uploadIssueAssetFiles(files, bucketId, itemId);
      }}
      onSubmitModificationRequestIssue={async (
        modificationRequestItemId: string,
        inputComment: string,
        bucketId: string | null,
        files: Array<FileReference>,
      ) =>
        await dependency.modificationRequestUsecase.createModificationRequestItemIssue(
          modificationRequestItemId,
          inputComment,
          bucketId,
          files.map((file) => file.id),
        )
      }
      onSubmitModificationRequestIssueComment={(issueId: string, comment: string) =>
        dependency.modificationRequestUsecase.createModificationRequestItemIssueComment(issueId, comment)
      }
      onRequestResolveModificationRequestIssue={(id: string) =>
        dependency.modificationRequestUsecase.resolveModificationRequestIssue(id)
      }
      onRequestReopenModificationRequestIssue={(id: string) =>
        dependency.modificationRequestUsecase.reopenModificationRequestIssue(id)
      }
      onRefetchCreation={async () => {
        dependency.showCreationUsecase.disposeCreation();
        dependency.modificationRequestUsecase.dispose();

        await dependency.showCreationUsecase
          .prepareCreation(match.params.creationId)
          .then(() => dependency.modificationRequestUsecase.prepare());
      }}
      // Send And Download Creative
      fetchFacebookAdAccounts={async () => dependency.sendCreativeToFacebookUsecase.fetchAdAccounts()}
      fetchAmazonAdAccounts={async () =>
        dependency.integrateAmazonAdAccountUsecase.fetchIntegratedAmazonAdAccounts(organizationId)
      }
      fetchAmazonUploadedCreatives={async (creativeId: string) =>
        dependency.integrateAmazonAdAccountUsecase.fetchUploadedAmazonCreatives(creativeId, organizationId)
      }
      isUploadedToAmazon={state.amazonUploadedCreative}
      fetchYahooAdAccounts={async () =>
        dependency.integrateYahooAdAccountUsecase.fetchIntegratedYahooAdAccounts(organizationId)
      }
      fetchYahooUploadedCreatives={async (creativeId: string) =>
        dependency.integrateYahooAdAccountUsecase.fetchUploadedYahooCreatives(creativeId, organizationId)
      }
      isUploadedToYahoo={state.yahooUploadedCreative}
      fetchTiktokAdAccounts={async () =>
        dependency.integrateTiktokAdAccountUsecase.fetchIntegratedTiktokAdAccounts(organizationId)
      }
      fetchTiktokUploadedCreatives={async (creativeId: string) =>
        dependency.integrateTiktokAdAccountUsecase.fetchUploadedTiktokCreatives(creativeId, organizationId)
      }
      isUploadedToTiktok={state.tiktokUploadedCreative}
      onClickAnyDeliverableDownloadButton={(
        _: any,
        deliverable: {
          creative: {
            file: Pick<File, 'id' | 'url' | 'mimeType'>;
          };
          attachedThumbnail?: {
            file: Pick<File, 'id' | 'url' | 'mimeType'>;
          } | null;
        },
        type,
      ) => {
        if (type == 'creative') {
          dependency.showCreationUsecase.downloadLatestDeliverableCreative(deliverable);
        } else if (type === 'attachedThumbnail') {
          dependency.showCreationUsecase.downloadAttachedThumbnail(deliverable);
        }
      }}
      onRequestSendingCreativeToFacebook={(
        _: any,
        deliverable: DeliverableForCraetionDetailFragment,
        selected: FacebookAdAccount,
      ) => dependency.sendCreativeToFacebookUsecase.sendCreative(deliverable, selected)}
      onRequestSendingCreativeToYoutube={(
        _: any,
        deliverable: DeliverableForCraetionDetailFragment,
        selected: YoutubeChannel,
      ) => dependency.sendCreativeToYoutubeUsecase.sendCreative(deliverable, selected)}
      onRequestSendingCreativeToAmazon={(
        _: any,
        deliverable: DeliverableForCraetionDetailFragment,
        selected: AmazonAdAccount,
      ) =>
        dependency.integrateAmazonAdAccountUsecase.sendCreative(
          deliverable,
          selected,
          state.creation.creation?.projectId || null,
        )
      }
      onRequestSendingCreativeToYahoo={(
        _: any,
        deliverable: DeliverableForCraetionDetailFragment,
        selected: YahooAdAccount,
      ) =>
        dependency.integrateYahooAdAccountUsecase.sendCreative(
          deliverable,
          selected,
          state.creation.creation?.projectId || null,
        )
      }
      onRequestSendingCreativeToTiktok={(
        _: any,
        deliverable: DeliverableForCraetionDetailFragment,
        selected: TiktokAdAccount,
      ) =>
        dependency.integrateTiktokAdAccountUsecase.sendCreative(
          deliverable,
          selected,
          state.creation.creation?.projectId || null,
        )
      }
      onRequestAddYoutubeChannel={(_: any) => dependency.sendCreativeToYoutubeUsecase.addConnectedChannel()}
      onApproveModificationRequest={async (modificationRequestId: string) => {
        dependency.modificationRequestUsecase.dispose();

        await dependency.modificationRequestUsecase.approveModificationRequest(modificationRequestId);
        dependency.showCreationUsecase
          .prepareCreation(match.params.creationId)
          .then(() => dependency.modificationRequestUsecase.prepare());
      }}
      onMount={() => {
        dependency.authenticationUsecase.needSignedIn();
        dependency.showCreationUsecase.prepareCreation(match.params.creationId).then(() => {
          dependency.modificationRequestUsecase.prepare();
        });
        dependency.integrateTiktokAdAccountUsecase.fetchAccessibleTiktokAdAccounts();
      }}
      onUnmount={() => {
        dependency.showCreationUsecase.disposeCreation();
        dependency.modificationRequestUsecase.dispose();
      }}
      // Kaizen Pay Widget
      language={state.language}
      email={state.authentication.signedInUser!.email!}
      onPurchasedPoint={async (point: number) => {
        if (point && point > 0) {
          await dependency.organizationUsecase.addPurchasedPointBalance(point);
          await dependency.modificationRequestUsecase.submitModificationRequest();
        }
      }}
      prepareUser={dependency.authenticationUsecase.fetchDirectorUser}
    />
  );
};

export default CreationDetailPage;
